import React, { useEffect, useState } from 'react';
import { StatusFormSetting } from '../AssetTabs';
import { closeTriggerKeyValuePair, openTriggerKeyValuePair } from '../../SiteSummary/constants';

interface FieldState {
    [key: string]: string;
}

type SetPageFunction = React.Dispatch<React.SetStateAction<{ [key: string]: FieldState; }>>;
type SetTabPageFunction = React.Dispatch<React.SetStateAction<string>>
type SetChangeFieldFunction = React.Dispatch<React.SetStateAction<{
    [formName: string]: Set<string>;
}>>
type DropdownOption = {
    text: string;
    value: number;
    status?: any;
};

interface PFCFormProps {
    formState: { [key: string]: FieldState };
    setFormState: SetPageFunction;
    setTabDivHeight: SetTabPageFunction;
    PFCFormData: StatusFormSetting[] | undefined;
    changedFields: { [formName: string]: Set<string> };
    setChangedFields: SetChangeFieldFunction;
    formNames: string;
    isToggle: boolean;
    description?: string;
}
interface InitialValues {
    [formName: string]: {
        [fieldName: string]: string;
    };
}


const PFCForm: React.FC<PFCFormProps> = ({ formState, setFormState, setTabDivHeight, PFCFormData, changedFields, setChangedFields, formNames, isToggle, description }) => {
    const [initialValues, setInitialValues] = useState<{ [formName: string]: { [fieldName: string]: string } }>({});
    const FormName = formNames

    useEffect(() => {
        setTabDivHeight("1250px");
    }, []);

    useEffect(() => {

        const initialValue = {};

        Object.keys(formState).forEach(formName => {
            initialValue[formName] = {};
            Object.keys(formState[formName]).forEach(fieldName => {
                initialValue[formName][fieldName] = formState[formName][fieldName];
            });
        });
        setInitialValues(initialValue);
        setChangedFields({})

    }, [formNames, isToggle]);


    const isFieldGrayedOut = (fieldName: string): boolean => {
        const openTrigger = formState[FormName]?.openTrigger;
        const closeTrigger = formState[FormName]?.closeTrigger;

        const getOpenTriggerValues = (): number[] => {
            return openTriggerKeyValuePair.map(item => item.value);
        };

        const getCloseTriggerValues = (): number[] => {
            return closeTriggerKeyValuePair.map(item => item.value);
        };

        const normalize = (val: string) =>
            val?.toLowerCase().replace(/[:]/g, "").replace(/\s+/g, "").trim();

        const currentField = PFCFormData?.find(field => field.name === fieldName);
        const currentLabelId = currentField?.id ?? 0;

        // Get Open Trigger Label
        const openTriggerField = PFCFormData?.find(field => field.name === "openTrigger");
        const openTriggerOptions = openTriggerField?.options as DropdownOption[] | undefined;

        const normalizedOpenTriggerLabel = normalize(
            openTriggerOptions?.find(opt => opt.value.toString() === openTrigger)?.text ?? ""
        );
        const openTriggerId = openTriggerKeyValuePair.find(
            item => normalize(item.key) === normalizedOpenTriggerLabel
        )?.value;

        // Get Close Trigger Label
        const closeTriggerField = PFCFormData?.find(field => field.name === "closeTrigger");
        const closeTriggerOptions = closeTriggerField?.options as DropdownOption[] | undefined;

        const normalizedCloseTriggerLabel = normalize(
            closeTriggerOptions?.find(opt => opt.value.toString() === closeTrigger)?.text ?? ""
        );

        const closeTriggerIds = closeTriggerKeyValuePair
            .filter(item => normalize(item.key) === normalizedCloseTriggerLabel)
            .map(item => item.value);

        const openTriggerRelatedLabels = getOpenTriggerValues();
        const closeTriggerRelatedLabels = getCloseTriggerValues();

        if (openTriggerRelatedLabels.includes(currentLabelId)) {
            return currentLabelId !== openTriggerId;
        }

        if (closeTriggerRelatedLabels.includes(currentLabelId)) {
            return !closeTriggerIds.includes(currentLabelId); // ← allow multiple "time" values
        }

        return false;
    };



    // Handle input change for a specific field in a specific form
    const handleInputChange = (formName: string, fieldName: string, value: string) => {

        setFormState((prevFormState) => ({
            ...prevFormState,
            [formName]: {
                ...prevFormState[formName],
                [fieldName]: value,
            },
        }));

        setChangedFields((prevChangedFields) => ({
            ...prevChangedFields,
            [formName]: new Set<string>([...(prevChangedFields[formName] || []), fieldName]),
        }));
    };


    // Function to identify change in field value
    const handleFieldChange = (formName: string, fieldName: string, newValue: string) => {
        const initialValue = initialValues[formName]?.[fieldName];
        if (initialValue !== newValue) {
            setChangedFields((prevChangedFields) => ({
                ...prevChangedFields,
                [formName]: new Set<string>([...(prevChangedFields[formName] || []), fieldName]),
            }));
        } else if (initialValue === newValue) {
            setChangedFields((prevChangedFields) => {
                const updatedFields = new Set<string>(prevChangedFields[formName] || []);
                updatedFields.delete(fieldName); // Remove the field name if the value reverts back to the initial value
                return {
                    ...prevChangedFields,
                    [formName]: updatedFields,
                };
            });
        }
    };

    return (
        <div style={{ paddingLeft: "15px" }}>
            {/* Plunger Form */}
            <div style={{ display: 'flex', flexDirection: 'row', columnGap: '30px', rowGap: "6px", flexWrap: 'wrap' }}>
                {PFCFormData?.map(({ id, value, name, label, type, options, enable, description, isGrayedOut, unit }) => {
                    const shouldGrayOut = isGrayedOut

                    return (
                        <div key={id} style={{ flexBasis: '30%' }}>
                            {enable && (
                                <div className="tooltip-container">
                                    <label
                                        style={{ display: 'block', marginBottom: '2px', marginLeft: "3px" }}
                                    >
                                        {label}
                                    </label>
                                    {description && (<span className={name === "PFC1_PrimaryMode" || name === "PFC1_SecondaryMode" ? "tooltip-text-below" : "tooltip-text"}>{description}</span>)}
                                </div>
                            )}
                            {type !== "dropdown" ? (
                                <div className="relative w-full">
                                    {/* Background fake text with unit */}
                                    <div
                                        className="absolute top-0 left-0 w-full h-full pointer-events-none flex items-center justify-end pr-2"
                                        style={{
                                            color: shouldGrayOut
                                                ? "#4A5463" : "var(--Primary-Indigo-500, #0094BD)",
                                            fontSize: "18px",
                                            padding: "6px",
                                            borderRadius: "8px",
                                            textAlign: "right",
                                        }}
                                    >
                                        {unit ? `| ${unit}` : ""}
                                    </div>

                                    {/* Actual input */}
                                    <input
                                        type={name.toLowerCase() === "ccid" ? "string" : "number"}
                                        value={
                                            formState[FormName] && formState[FormName][name] !== undefined
                                                ? formState[FormName][name]
                                                : value
                                        }
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            handleInputChange(FormName, name, newValue);
                                            handleFieldChange(FormName, name, newValue);
                                        }}
                                        style={{
                                            display: enable ? "" : "none",
                                            padding: "6px",
                                            paddingRight: unit ? "5rem" : "6px",
                                            color: "white",
                                            borderRadius: "8px",
                                            textAlign: "right",
                                            backgroundColor: changedFields[FormName]?.has(name)
                                                ? "rgb(208 196 64)"
                                                : shouldGrayOut
                                                    ? "#131F2E"
                                                    : "rgb(5 17 35)",
                                            width: "100%",
                                            outline: shouldGrayOut
                                                ? "1px solid #4A5463"
                                                : "1px solid var(--Primary-Indigo-500, #0094BD)",
                                        }}
                                        placeholder={label}
                                        disabled={isGrayedOut}
                                    />
                                </div>

                            ) : (
                                <select
                                    disabled={isGrayedOut}
                                    value={formState[FormName] ? formState[FormName][name] : value}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        handleInputChange(FormName, name, newValue);
                                        handleFieldChange(FormName, name, newValue);
                                    }}
                                    style={{
                                        visibility: enable ? "visible" : "hidden",
                                        padding: "6px",
                                        color: 'white',
                                        borderRadius: '8px',
                                        textAlign: 'left',
                                        backgroundColor: changedFields[FormName]?.has(name)
                                            ? "rgb(208 196 64)"
                                            : (shouldGrayOut)
                                                ? "#131F2E"
                                                : "rgb(5 17 35)",

                                        width: "100%",
                                        outline: shouldGrayOut ? "1px solid #4A5463" : "1px solid var(--Primary-Indigo-500, #0094BD)"
                                    }}
                                >
                                    {options?.map((option: any) => (
                                        <option key={option.value} value={option.value}>
                                            {option.text}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    );
                })}

            </div>
        </div>
    );
};

export default PFCForm;
